import { Avatar } from "@dropbox/dig-components/avatar";
import { Box } from "@dropbox/dig-foundations";
import { getInitialsFromPerson } from "@mirage/shared/account";
import { Trans } from "@mirage/translations";
import React, { Fragment } from "react";
import styles from "./PersonMetadata.module.css";

import { UserProfileHoverCard } from "@mirage/mosaics/BaseProfilePage/UserProfileHoverCard";
import type { PersonObject } from "@mirage/service-people/types";
import type {
  AuthorInfo,
  CuratorUser,
} from "@mirage/shared/search/search-result";
import type { StackItemUser } from "@mirage/shared/search/stack-item";
import type { I18nCreateByKey } from "@mirage/shared/translations/I18nCreateByKey";

type PersonType = AuthorInfo | StackItemUser | CuratorUser | undefined | null;

export type PersonMetadataProps = {
  i18nKey?: I18nCreateByKey;
  person: PersonType;
  onClickPerson?: (person: PersonObject) => void;
  hideAvatar?: boolean;
};

const PersonMetadataWithProfileHoverCard = ({
  person,
  handleClickPerson,
  children,
}: {
  person: PersonType;
  handleClickPerson: (person: PersonType) => void;
  children?: React.ReactNode;
}) => {
  return (
    <UserProfileHoverCard personEmail={person?.email}>
      <Box
        as="button"
        onClick={(e: React.SyntheticEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleClickPerson(person);
        }}
        fontFamily="Text"
        color={{
          default: "Text Subtle",
          hover: "Text Base",
        }}
        className={styles.button}
      >
        {children}
      </Box>
    </UserProfileHoverCard>
  );
};

export const PersonMetadata = ({
  i18nKey,
  person,
  onClickPerson,
  hideAvatar = false,
}: PersonMetadataProps) => {
  const displayName = person?.displayName || person?.email;
  const initials = getInitialsFromPerson({
    displayName: person?.displayName,
    email: person?.email || "",
  });

  const handleClickPerson = (person: AuthorInfo | undefined | null) => {
    const { email = "", displayName, profilePhotoUrl } = person || {};
    onClickPerson?.({
      email,
      displayName,
      profilePhotoUrl,
    });
  };

  return (
    <div className={styles.container}>
      {!hideAvatar && (
        <UserProfileHoverCard personEmail={person?.email}>
          <Avatar
            size="xsmall"
            hasNoOutline
            src={person?.profilePhotoUrl}
            alt={initials}
            className={styles.avatar}
          >
            {initials}
          </Avatar>
        </UserProfileHoverCard>
      )}
      <span className="meticulous-redact-recording">
        {i18nKey ? (
          <Trans
            i18nKey={i18nKey}
            values={{ name: displayName }}
            components={{
              tag: onClickPerson ? (
                <PersonMetadataWithProfileHoverCard
                  person={person}
                  handleClickPerson={handleClickPerson}
                />
              ) : (
                <Fragment />
              ),
            }}
          />
        ) : onClickPerson ? (
          <UserProfileHoverCard personEmail={person?.email}>
            <Box
              as="button"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleClickPerson(person);
              }}
              fontFamily="Text"
              color={{
                default: "Text Subtle",
                hover: "Text Base",
              }}
              className={styles.button}
            >
              {displayName}
            </Box>
          </UserProfileHoverCard>
        ) : (
          <UserProfileHoverCard personEmail={person?.email}>
            {displayName}
          </UserProfileHoverCard>
        )}
      </span>
    </div>
  );
};
